import React, { useState, useEffect } from 'react';
import api from '../../api';
import { IconCheck, IconAlert } from '../../components/Icons';

/* ── Single setting row ────────────────────────────────────────── */
function SettingRow({ label, description, settingKey, type = 'text', placeholder, current, onSave }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const isSet = current?.masked ? true : !!current?.value;
  const hint = current?.masked ? current.hint : current?.value;

  async function handleSave(e) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true); setError('');
    try {
      await onSave(settingKey, value.trim());
      setValue('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function handleClear() {
    setSaving(true);
    try { await onSave(settingKey, ''); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ padding: '18px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Label + status */}
      <div style={{ flex: '0 0 220px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
          <span style={{ fontWeight: 600, fontSize: 13.5, color: '#0f172a' }}>{label}</span>
          {isSet
            ? <span style={{ fontSize: 11, background: '#dcfce7', color: '#166534', borderRadius: 99, padding: '1px 7px', fontWeight: 600 }}>Connected</span>
            : <span style={{ fontSize: 11, background: '#f1f5f9', color: '#94a3b8', borderRadius: 99, padding: '1px 7px' }}>Not set</span>
          }
        </div>
        <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>{description}</div>
        {isSet && (
          <div style={{ marginTop: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontSize: 12, color: '#0D9488', fontFamily: 'monospace' }}>{hint}</span>
            <button onClick={handleClear} style={{ marginLeft: 4, fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>remove</button>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSave} style={{ flex: 1, minWidth: 200, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <input
            type={type}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={isSet ? `Replace current value…` : placeholder}
            style={{ width: '100%', fontFamily: type === 'password' ? 'monospace' : undefined }}
            autoComplete="off"
          />
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5 }}>
              <IconAlert width={12} height={12} stroke="#ef4444" />
              <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
            </div>
          )}
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={saving || !value.trim()}
          style={{ flexShrink: 0, marginTop: 1 }}
        >
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
        </button>
      </form>
    </div>
  );
}

/* ── Section wrapper ───────────────────────────────────────────── */
function Section({ title, subtitle, children }) {
  return (
    <div className="card mb-5">
      <div className="card-header">
        <div>
          <div style={{ fontWeight: 700 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      <div className="card-body" style={{ padding: '0 24px' }}>
        {children}
      </div>
    </div>
  );
}

/* ── Main view ─────────────────────────────────────────────────── */
export default function SettingsView() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoadError('');
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to load settings';
      setLoadError(`${err.response?.status ?? ''} ${msg}`.trim());
    } finally { setLoading(false); }
  }

  async function saveSetting(key, value) {
    await api.put('/settings', { [key]: value });
    const { data } = await api.get('/settings');
    setSettings(data);
  }

  if (loading) return <div className="loading">Loading settings…</div>;

  return (
    <div style={{ maxWidth: 780 }}>
      {loadError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#b91c1c' }}>
          <IconAlert width={15} height={15} stroke="#b91c1c" />
          <span>Could not load settings: <strong>{loadError}</strong> — check the API or your permissions.</span>
        </div>
      )}

      {/* AI */}
      <Section
        title="AI & Intelligence"
        subtitle="Powers inventory scanning, voice queries, and smart suggestions"
      >
        <SettingRow
          label="Anthropic API Key"
          description="Required for walk-in photo scanning and voice AI. Get yours at console.anthropic.com"
          settingKey="anthropic_api_key"
          type="password"
          placeholder="sk-ant-api03-…"
          current={settings.anthropic_api_key}
          onSave={saveSetting}
        />
      </Section>

      {/* Weather & Signals */}
      <Section
        title="Weather & External Signals"
        subtitle="Pulls daily forecasts to help predict demand and flag supply risks"
      >
        <SettingRow
          label="OpenWeather API Key"
          description="Free tier available at openweathermap.org — used for daily 7-day forecasts."
          settingKey="openweather_api_key"
          type="password"
          placeholder="a1b2c3d4e5f6…"
          current={settings.openweather_api_key}
          onSave={saveSetting}
        />
        <SettingRow
          label="Location (City)"
          description='City for weather lookups — e.g. "Lahaina,HI,US" or "Honolulu,HI,US". Overrides lat/lon if set.'
          settingKey="weather_location"
          placeholder="Lahaina,HI,US"
          current={settings.weather_location}
          onSave={saveSetting}
        />
      </Section>

      {/* POS */}
      <Section
        title="Toast POS"
        subtitle="Syncs sales data nightly and pushes approved orders to Toast"
      >
        <SettingRow
          label="Toast API Key"
          description="From Toast Admin → Integrations → API. Used for nightly sales sync."
          settingKey="toast_api_key"
          type="password"
          placeholder="your Toast API key"
          current={settings.toast_api_key}
          onSave={saveSetting}
        />
        <SettingRow
          label="Toast Restaurant GUID"
          description="Your Toast restaurant identifier — found in Toast Admin → Restaurant Info."
          settingKey="toast_restaurant_guid"
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          current={settings.toast_restaurant_guid}
          onSave={saveSetting}
        />
      </Section>

      {/* SMS */}
      <Section
        title="Twilio SMS"
        subtitle="Sends availability requests to local farmers and suppliers"
      >
        <SettingRow
          label="Account SID"
          description="From console.twilio.com — Account Info panel."
          settingKey="twilio_account_sid"
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          current={settings.twilio_account_sid}
          onSave={saveSetting}
        />
        <SettingRow
          label="Auth Token"
          description="Twilio auth token — keep this secret."
          settingKey="twilio_auth_token"
          type="password"
          placeholder="your auth token"
          current={settings.twilio_auth_token}
          onSave={saveSetting}
        />
        <SettingRow
          label="Twilio Phone Number"
          description="The number SMS messages are sent from. Format: +18085551234"
          settingKey="twilio_phone_number"
          placeholder="+18085551234"
          current={settings.twilio_phone_number}
          onSave={saveSetting}
        />
      </Section>

      {/* Invoice OCR */}
      <Section
        title="Google Cloud Vision"
        subtitle="Optional: used for walk-in photo scanning and label recognition. Invoice scanning uses AI (Anthropic) and does not require this key."
      >
        <SettingRow
          label="API Key"
          description="From console.cloud.google.com → APIs → Cloud Vision API → Credentials. Leave blank if not using walk-in photo scanning."
          settingKey="google_vision_api_key"
          type="password"
          placeholder="AIzaSy…"
          current={settings.google_vision_api_key}
          onSave={saveSetting}
        />
      </Section>

      {/* Email */}
      <Section
        title="Email Alerts"
        subtitle="Sends daily below-par alerts and order summaries"
      >
        <SettingRow
          label="Alert Email Address"
          description="Where daily inventory alerts and order summaries are sent."
          settingKey="alert_email"
          type="email"
          placeholder="chef@restaurant.com"
          current={settings.alert_email}
          onSave={saveSetting}
        />
      </Section>

      {/* Restaurant info */}
      <Section title="Restaurant Info">
        <SettingRow
          label="Restaurant Name"
          description="Displayed in reports and email subjects."
          settingKey="restaurant_name"
          placeholder="Paradise Restaurant"
          current={settings.restaurant_name}
          onSave={saveSetting}
        />
        <SettingRow
          label="Timezone"
          description="Used for daily summaries and cron schedules."
          settingKey="timezone"
          placeholder="Pacific/Honolulu"
          current={settings.timezone}
          onSave={saveSetting}
        />
      </Section>

      <p className="text-xs text-gray" style={{ marginTop: 4, lineHeight: 1.6 }}>
        All API keys are stored in your private database. Secret keys are masked after saving and never exposed in API responses.
      </p>

    </div>
  );
}
